// @flow

import * as React from 'react';
import * as Types from '@ta/trips.trip-types';
import type { TripsError, TripsScopedError } from '@ta/trips.trip-errors';
import { AddItemToTrip } from '@ta/trips.graphql';
import { useMutation } from 'urql';
import { getItemByReference } from '@ta/trips.trip-util';
import { usePixelCallback } from '@ta/platform.monikers';

export type SaveState = {|
  fetching: boolean,
  result?: {
    trip: Types.Trip,
    tripItem: Types.TripItem,
  },
  error?: TripsScopedError,
|};

export type SaveFunction = number => Promise<[?Types.Trip, ?TripsError]>;
export type UseSaveResponse = [SaveState, SaveFunction];

const useSave = (object: ?Types.TripSaveObject): UseSaveResponse => {
  const [, execute] = useMutation(AddItemToTrip);
  const [state, setState] = React.useState<SaveState>({ fetching: false });
  const fireSaveToTrip = usePixelCallback('SAVE_TO_TRIP');

  if (!object) {
    return [state, () => Promise.resolve([null, null])];
  }
  // to work around flow awkwardness
  const trueObject: Types.TripSaveObject = object;

  const save = (tripId: number) => {
    setState({ fetching: true });
    return execute({
      tripId,
      referenceId: `${trueObject.id}`,
      referenceType: trueObject.type,
      allowDuplicates: false,
    })
      .then(result => {
        /* $FlowFixGraphql (WP-1357) */
        const trip: ?Types.Trip = result?.data?.addItemToTrip?.trip;
        /* $FlowFixGraphql (WP-1357) */
        const error: ?TripsError = result?.data?.addItemToTrip?.errors?.[0]?.error;

        if (trip) {
          const item = getItemByReference(trip, trueObject);
          if (item) {
            fireSaveToTrip();
            setState({ fetching: false, result: { trip, tripItem: item } });
          } else if (trueObject.type === 'bundleSave') {
            // return dummy trip item for the bundle
            setState({
              fetching: false,
              result: { trip, tripItem: { __typename: 'TripItem', id: 0, comments: [] } },
            });
          } else {
            setState({ fetching: false, error: { query: 'ADD_ITEM_TO_TRIP', tripsError: error } });
          }
        } else {
          setState({ fetching: false, error: { query: 'ADD_ITEM_TO_TRIP', tripsError: error } });
        }

        return [trip, error];
      })
      .catch(e => {
        console.error(e);
        setState({ fetching: false, error: { query: 'ADD_ITEM_TO_TRIP' } });
        return [null, null];
      });
  };

  return [state, save];
};

export default useSave;
